import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { useAuthContext } from "providers/Auth";
import type { ColumnVisibilityItem } from "providers/Auth";
import { mergeSavedColumns } from "shared/utils/column-order";

export const COLUMNS_SAVE_ERROR = "Couldn't save your column order. Try again.";

const columnsEqual = (
  a: ColumnVisibilityItem[],
  b: ColumnVisibilityItem[]
): boolean => {
  if (a.length !== b.length) return false;
  return a.every(
    (col, i) => col.id === b[i].id && col.checked === b[i].checked
  );
};

/**
 * Manages per-user, per-tab column visibility + ordering.
 *
 * Two save modes:
 *   - `flush()`        — save-on-close (call when the menu closes); skips the
 *                        network request if nothing changed since the last save
 *   - `commitNow(next)`— update state AND save immediately (for in-table
 *                        interactions like hide-column or drag-reorder)
 *
 * `reset()` puts the tab back to Boom's standard order and shown/hidden setup and saves it.
 * When a save fails the columns stay where the user put them and an error toast shows; the next
 * visit loads the last saved setup.
 */
export const useColumnVisibility = (
  tabKey: string,
  defaults: ColumnVisibilityItem[]
) => {
  const { auth, refreshAuthUserDashboardState } = useAuthContext();

  const [columns, setColumns] = useState<ColumnVisibilityItem[]>(() =>
    mergeSavedColumns(auth?.columns_visibility?.[tabKey], defaults)
  );
  const lastSavedRef = useRef<ColumnVisibilityItem[]>(columns);

  useEffect(() => {
    const next = mergeSavedColumns(
      auth?.columns_visibility?.[tabKey],
      defaults
    );
    setColumns(next);
    lastSavedRef.current = next;
  }, [tabKey, auth?.columns_visibility, defaults]);

  // mutateAsync is stable across renders, unlike the mutation object, so callbacks stay memoized.
  const { mutateAsync: saveColumnsRequest } = useMutation({
    mutationKey: ["COLUMNS_VISIBILITY", "UPDATE", auth?.id, tabKey],
    mutationFn: (allTabs: Record<string, ColumnVisibilityItem[]>) =>
      instance.patch(`/portal/v1/users/${auth?.id}`, {
        columns_visibility: allTabs
      })
  });

  const save = useCallback(
    async (nextColumns: ColumnVisibilityItem[]) => {
      if (columnsEqual(nextColumns, lastSavedRef.current)) return;

      // Only this tab: the API merges it into the stored tabs, so a stale copy of the other
      // tables' columns (another browser tab, a cached /auth) is never written back.
      const payload = { [tabKey]: nextColumns };

      try {
        await saveColumnsRequest(payload);
        lastSavedRef.current = nextColumns;
        refreshAuthUserDashboardState();
      } catch {
        toast.error(COLUMNS_SAVE_ERROR);
      }
    },
    [tabKey, saveColumnsRequest, refreshAuthUserDashboardState]
  );

  const flush = useCallback(() => save(columns), [columns, save]);

  const commitNow = useCallback(
    (nextColumns: ColumnVisibilityItem[]) => {
      setColumns(nextColumns);
      return save(nextColumns);
    },
    [save]
  );

  const reset = useCallback(() => commitNow(defaults), [commitNow, defaults]);

  return {
    columns,
    setColumns,
    flush,
    commitNow,
    reset
  };
};
